import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-transparent text-[13px] font-medium tracking-[0.2px] transition-[background-color,border-color,color,box-shadow,transform] duration-150 focus-visible:outline-none focus-visible:border-fc-accent/70 focus-visible:shadow-fc-focus active:translate-y-px disabled:pointer-events-none disabled:opacity-40 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-fc-accent text-fc-ink font-semibold shadow-fc-primary hover:brightness-[1.08]",
        muted: "border-fc-stroke-2 bg-white/[0.06] text-fc-txt2 hover:bg-white/[0.12] hover:text-white",
        link: "text-fc-accent-bright underline-offset-4 hover:underline",

        destructive: "border-fc-red/30 bg-fc-red/[0.14] text-fc-red-soft hover:bg-fc-red/[0.24] hover:text-fc-red-bright",
        warning: "border-fc-yellow/35 bg-fc-yellow/[0.12] text-fc-yellow hover:bg-fc-yellow/[0.2]",
        success: "border-fc-green/35 bg-fc-green/[0.12] text-fc-green hover:bg-fc-green/[0.2]",
        info: "border-fc-accent/45 bg-fc-accent/[0.16] text-fc-accent-bright hover:bg-fc-accent/[0.26]",
        secondary: "border-fc-stroke-2 bg-white/[0.06] text-white hover:bg-white/[0.14] hover:border-white/[0.22]",

        ghost: "text-fc-txt2 hover:bg-fc-hover hover:text-white",
        "ghost-destructive": "text-fc-red-soft hover:bg-fc-red/[0.14] hover:text-fc-red-bright",
        "ghost-warning": "text-fc-yellow hover:bg-fc-yellow/[0.12]",
        "ghost-success": "text-fc-green hover:bg-fc-green/[0.12]",
        "ghost-info": "text-fc-accent-bright hover:bg-fc-accent/[0.14]",
        "ghost-secondary": "text-white hover:bg-fc-hover",
        "ghost-muted": "text-fc-txt3 hover:bg-fc-hover hover:text-fc-txt2",

        outline: "border-fc-accent/45 bg-fc-accent/[0.08] text-fc-accent-bright hover:bg-fc-accent/[0.2]",
        "outline-destructive": "border-fc-red/35 bg-fc-red/[0.08] text-fc-red-soft hover:bg-fc-red/[0.2] hover:text-fc-red-bright",
        "outline-warning": "border-fc-yellow/35 bg-fc-yellow/[0.08] text-fc-yellow hover:bg-fc-yellow/[0.18]",
        "outline-success": "border-fc-green/35 bg-fc-green/[0.08] text-fc-green hover:bg-fc-green/[0.18]",
        "outline-info": "border-fc-accent/45 bg-fc-accent/[0.08] text-fc-accent-bright hover:bg-fc-accent/[0.2]",
        "outline-secondary": "border-fc-stroke-2 bg-transparent text-white hover:bg-white/[0.1]",
        "outline-muted": "border-fc-stroke-2 bg-white/[0.04] text-fc-txt2 hover:bg-white/[0.1] hover:text-white",
      },
      size: {
        default: "h-10 px-4 py-2",
        inline: "h-5 rounded-md px-1.5 text-2xs tracking-[1px]",
        xs: "h-7 rounded-md px-2.5 text-xs",
        sm: "h-[34px] rounded-lg px-3.5 text-[12.5px]",
        lg: "h-11 rounded-lg px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
